import { languages, type Language } from '@osteopro/shared';
import type { i18n } from 'i18next';
import { z } from 'zod';

/**
 * Messages de validation zod dans la langue de l'interface.
 *
 * Les schémas viennent de `@osteopro/shared` : ils sont écrits une fois et exécutés tels quels des
 * deux côtés, sans message littéral. Sans configuration, zod produit donc ses libellés anglais par
 * défaut — c'est ce qui affichait « Invalid ISO date » sous un champ date d'un formulaire français.
 *
 * `z.config()` est **global au processus** : une seule pose suffit pour tous les schémas, y compris
 * ceux déjà construits par le paquet partagé, puisque le message est résolu au moment de l'échec et
 * non à la construction. C'est aussi pourquoi cela n'a sa place qu'ici, une fois, et pas dans
 * chaque formulaire.
 */

/** Une entrée par langue du produit : `languages` et cette table ne peuvent pas diverger. */
const ZOD_LOCALES: Record<Language, () => { localeError: z.core.$ZodErrorMap }> = {
  fr: z.locales.fr,
  de: z.locales.de,
  en: z.locales.en,
};

function isLanguage(value: string): value is Language {
  return (languages as readonly string[]).includes(value);
}

/**
 * `fr-BE` comme `fr` : i18next peut rendre une étiquette régionale, zod n'en connaît que la base.
 * La langue peut être absente tant que `init` n'a pas rendu la main — le français, langue par
 * défaut du produit, sert alors de repli, et l'événement `languageChanged` corrige aussitôt.
 */
export function applyZodLocale(language: string | undefined): void {
  const base = (language ?? '').slice(0, 2).toLowerCase();
  const locale = isLanguage(base) ? ZOD_LOCALES[base] : ZOD_LOCALES.fr;
  z.config(locale());
}

/** Pose la locale courante puis suit i18next pour toute la durée de vie de l'application. */
export function syncZodLocale(instance: i18n): void {
  applyZodLocale(instance.language);
  instance.on('languageChanged', applyZodLocale);
}
